import Flex from 'UIKit/Box/Flex'
import Button from 'UIKit/Button'
import Heading from 'UIKit/Heading'
import Modal, { ModalBody, ModalHeader, ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text'
import { ProviderInfo } from 'config/types'
import { RowCenter } from 'layout/Components/Row'
import React, { useState } from 'react'
import { Trans } from 'react-i18next'
import { useAppSelector } from 'state'
import styled, { css } from 'styled-components'
import theme from 'theme'
import ProviderBannerItem from 'views/Home/components/ProviderBannerItem'

export interface ProviderModalProps {
  selectedProviders: string[]
  onProvidersChanged: (codes: string[]) => void
}

const ProviderModal: React.FC<ModalProps<ProviderModalProps>> = ({
  onDismiss,
  data: { onProvidersChanged, selectedProviders },
}) => {
  const providers = useAppSelector((state) => state.app.providers)

  const [selected, setSelected] = useState<string[]>(selectedProviders)

  const isEmptyState = selected.length === 0

  const handleSelected = (code: string) => {
    let _selected = [...selected]
    if (_selected.includes(code)) {
      _selected = _selected.filter((select) => select !== code)
    } else {
      _selected.push(code)
    }
    setSelected(_selected)
  }

  const handleSubmit = () => {
    const isDfferent =
      selected.length !== selectedProviders.length ||
      selected.filter((select) => !selectedProviders.includes(select)).length > 0

    if (isDfferent) {
      onProvidersChanged(selected)
    }
    onDismiss()
  }

  const handleClear = () => {
    setSelected([])
  }

  return (
    <StyledModal onDismiss={onDismiss}>
      <ModalHeader>
        <Heading scale="xs" ml="12px">
          <Trans>Filter by Provider</Trans>
        </Heading>
      </ModalHeader>
      <ModalBody borderRadius={theme.radii.modal} height="100%" py={['12px', '24px']}>
        <Flex flexWrap="wrap" maxHeight={['300px', '', '500px']} overflow="auto" px={['12px', '24px']}>
          {providers.length > 0 &&
            providers.map((provider: ProviderInfo) => {
              return (
                <StyledProvider
                  $active={selected.includes(provider.code)}
                  onClick={() => handleSelected(provider.code)}
                  provider={provider}
                  key={`item-provider-${provider.code}`}
                />
              )
            })}
        </Flex>
        <RowCenter flexDirection="column" mt="40px" px={['12px', '24px']}>
          <Button width="100%" mb="12px" disabled={selected.length > 20} onClick={handleSubmit}>
            {selected.length > 20 ? (
              <Text bold small>
                <Trans>Please select less than 20 providers</Trans>
              </Text>
            ) : (
              <Text bold small>
                <Trans>Show games</Trans>
              </Text>
            )}
          </Button>
          <Button
            onClick={handleClear}
            width="100%"
            style={{
              background: 'transparent',
            }}
            disabled={isEmptyState}
          >
            {!isEmptyState && (
              <Text small>
                <Trans>Clear</Trans>
              </Text>
            )}
          </Button>
        </RowCenter>
      </ModalBody>
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  ${({ theme }) => theme.mediaQueries.sm} {
    min-width: 456px !important;
  }

  overflow-y: hidden;
`

const StyledProvider = styled(ProviderBannerItem)<{ $active: boolean }>`
  cursor: pointer;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.radii.default};
  position: relative;
  box-sizing: border-box;

  .container-provider-item {
    background: transparent !important;
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      outline: 1px solid ${theme.colors.primary};

      &::before {
        content: ' ';
        width: 30px;
        height: 30px;
        background: url('/images/provider-icon-check.png');
        position: absolute;
        top: -1px;
        right: -1px;
        background-size: cover;
        z-index: 10;
      }
    `}

  &:hover {
    background: rgba(153, 153, 153, 0.16);
  }

  margin: 8px;
  width: calc(33% - 16px);
`

export default ProviderModal
